import React from 'react';
import { PocSectionData, Client, ClientGoal } from '../../types';
import { FEEDING_TREATMENT_CPT, SPEECH_TREATMENT_CPT } from '../../utils/clinicalDocDisplay';
import {
  Calendar,
  Target,
  ShieldCheck,
  Award,
  HeartHandshake,
  UserCheck,
  Trash2,
  FileText,
  AlertCircle
} from 'lucide-react';

interface PocWorkspaceProps {
  data: PocSectionData;
  onChange: (updated: PocSectionData) => void;
  client?: Client;
  canonicalGoals?: ClientGoal[];
  readOnly?: boolean;
}

export const PocWorkspace: React.FC<PocWorkspaceProps> = ({
  data,
  onChange,
  client,
  canonicalGoals = [],
  readOnly = false
}) => {
  const updateField = <K extends keyof PocSectionData>(field: K, value: PocSectionData[K]) => {
    onChange({
      ...data,
      [field]: value
    });
  };

  // Goals initialization from canonical goals or existing data
  const currentGoals = data.goals || (canonicalGoals.length > 0
    ? canonicalGoals.filter(cg => cg.status === 'ACTIVE' || cg.status === 'IN_PROGRESS').map(cg => ({
        id: cg.id,
        description: cg.description,
        baseline: cg.baseline_performance || '',
        current_percentage: cg.current_percentage || 0,
        target_percentage: cg.target_percentage || 80,
        status: cg.status || 'IN_PROGRESS',
        action: 'CONTINUE' as const,
        target_date: cg.target_date || ''
      }))
    : []);

  const addGoal = () => {
    updateField('goals', [
      ...currentGoals,
      {
        id: `GOL-NEW-${Date.now().toString(36).slice(-4).toUpperCase()}`,
        description: '',
        baseline: '',
        current_percentage: 0,
        target_percentage: 80,
        status: 'IN_PROGRESS',
        action: 'ADD',
        target_date: ''
      }
    ]);
  };

  const removeAddedGoal = (idx: number) => {
    // Only permit removing unsaved newly added goals; inherited canonical goals require explicit clinical disposition
    updateField('goals', currentGoals.filter((_, i) => i !== idx));
  };

  const updateGoal = (idx: number, field: string, val: any) => {
    const updated = [...currentGoals];
    updated[idx] = { ...updated[idx], [field]: val };
    updateField('goals', updated);
  };

  const setGoalAction = (idx: number, action: 'CONTINUE' | 'MODIFY' | 'MET' | 'DISCONTINUE') => {
    const updated = [...currentGoals];
    const g = updated[idx];
    let newStatus = g.status;
    if (action === 'MET') newStatus = 'MET';
    else if (action === 'DISCONTINUE') newStatus = 'DISCONTINUED';
    else if (action === 'CONTINUE' || action === 'MODIFY') newStatus = 'IN_PROGRESS';

    updated[idx] = {
      ...g,
      action,
      status: newStatus
    };
    updateField('goals', updated);
  };

  const setDiscipline = (discipline: 'SPEECH' | 'FEEDING') => {
    onChange({
      ...data,
      discipline,
      cpt_code: discipline === 'FEEDING' ? FEEDING_TREATMENT_CPT : SPEECH_TREATMENT_CPT
    });
  };
  const feeding = data.discipline === 'FEEDING';

  return (
    <div className="space-y-6 text-slate-200">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-slate-300">Discipline</span>
        {(['SPEECH', 'FEEDING'] as const).map((choice) => (
          <button
            key={choice}
            type="button"
            disabled={readOnly}
            onClick={() => setDiscipline(choice)}
            className={`px-3 py-1.5 rounded-lg text-xs font-bold border ${
              (feeding ? 'FEEDING' : 'SPEECH') === choice
                ? 'bg-sky-600 text-white border-sky-500'
                : 'bg-[#0a101b] text-slate-200 border-slate-600'
            }`}
          >
            {choice === 'SPEECH' ? 'Speech' : 'Feeding'}
          </button>
        ))}
        <span className="text-[10px] text-slate-400">
          Treatment CPT {feeding ? FEEDING_TREATMENT_CPT : (data.cpt_code || SPEECH_TREATMENT_CPT)}
        </span>
      </div>
      {/* 1. Recertification Period & History */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <Calendar className="w-4 h-4 text-sky-400" />
          <span>1. Recertification Period & Initial Therapy Date</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">
              POC Start Date *
            </label>
            <input
              type="date"
              disabled={readOnly}
              value={data.poc_start_date || new Date().toISOString().split('T')[0]}
              onChange={(e) => {
                const newStart = e.target.value;
                const d = new Date(newStart);
                d.setFullYear(d.getFullYear() + 1);
                const newEnd = d.toISOString().split('T')[0];
                updateField('poc_start_date', newStart);
                if (!data.poc_end_date) {
                  updateField('poc_end_date', newEnd);
                }
              }}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">
              POC End Date *
            </label>
            <input
              type="date"
              disabled={readOnly}
              value={
                data.poc_end_date ||
                (() => {
                  const d = new Date(data.poc_start_date || Date.now());
                  d.setFullYear(d.getFullYear() + 1);
                  return d.toISOString().split('T')[0];
                })()
              }
              onChange={(e) => updateField('poc_end_date', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">
              Original Therapy Start Date
            </label>
            <input
              type="date"
              disabled={readOnly}
              value={data.initial_eval_date || client?.poc_start_date || ''}
              onChange={(e) => updateField('initial_eval_date', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>
      </div>

      {/* 2. Progress Summary over Prior Cycle & Caregiver Engagement */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <HeartHandshake className="w-4 h-4 text-sky-400" />
          <span>2. Summary of Progress Over Prior Cycle & Caregiver Engagement</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1 uppercase tracking-wider">
              Clinical Progress Summary & Skill Generalization
            </label>
            <textarea
              disabled={readOnly}
              rows={3}
              value={data.progress_summary || ''}
              onChange={(e) => updateField('progress_summary', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1 uppercase tracking-wider">
              Caregiver Participation & Carryover in the Home
            </label>
            <textarea
              disabled={readOnly}
              rows={3}
              value={data.caregiver_participation || ''}
              onChange={(e) => updateField('caregiver_participation', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>
      </div>

      {/* 3. Continued Skilled Need & Medical Necessity */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-amber-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <ShieldCheck className="w-4 h-4 text-amber-400" />
          <span>3. Continued Skilled Need & Medical Necessity Statement</span>
        </div>

        <div>
          <label className="block text-[10px] font-semibold text-amber-200 mb-1">
            {feeding
              ? `Clinical Justification for Continued Feeding Therapy (CPT ${FEEDING_TREATMENT_CPT})`
              : `Clinical Justification for Continued Speech Therapy (CPT ${data.cpt_code || SPEECH_TREATMENT_CPT})`}
          </label>
          <textarea
            disabled={readOnly}
            rows={3}
            value={data.continued_skilled_need || ''}
            onChange={(e) => updateField('continued_skilled_need', e.target.value)}
            className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-amber-500 focus:ring-1 focus:ring-amber-500/30 focus:outline-none leading-relaxed italic disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
          />
        </div>
      </div>

      {/* 4. Active Goals Schedule & Canonical Decisions */}
      <div
        id="poc-section-goals"
        className={`p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3 transition-colors ${
          currentGoals.length === 0
            ? 'border-2 border-amber-500/60'
            : 'border border-slate-700/80'
        }`}
      >
        <div className="flex flex-wrap items-center justify-between gap-2 pb-2.5 border-b border-slate-700/60">
          <div className="flex flex-wrap items-center gap-2 text-cyan-400 font-bold text-xs uppercase tracking-wider">
            <div className="flex items-center space-x-2">
              <Target className="w-4 h-4 text-cyan-400" />
              <span>4. Plan of Care: Treatment Goals</span>
            </div>
            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded border border-slate-300 bg-slate-50 text-slate-700 uppercase tracking-wider">
              Required for Treatment
            </span>
            <span
              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border ${
                currentGoals.length === 0
                  ? 'bg-amber-50 text-amber-900 border-amber-300'
                  : 'bg-slate-50 text-slate-700 border-slate-300'
              }`}
            >
              {currentGoals.length} {currentGoals.length === 1 ? 'Goal' : 'Goals'}
            </span>
          </div>
          {!readOnly && (
            <button
              type="button"
              id="poc-add-goal-btn"
              onClick={addGoal}
              className="px-3 py-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-500 text-white shadow-xs text-xs font-bold transition-colors"
            >
              Add Goal
            </button>
          )}
        </div>

        {currentGoals.length > 0 && (
          <p className="text-[11px] text-slate-300 leading-relaxed">
            Inherited active goals require explicit clinical disposition (Continue, Modify, Met, or Discontinue).
          </p>
        )}

        {currentGoals.length === 0 ? (
          <div className="p-4 rounded-xl bg-amber-50 border border-amber-300 text-amber-900 text-xs flex items-center space-x-2 font-semibold">
            <AlertCircle className="w-4 h-4 text-amber-800 shrink-0" />
            <span>Treatment Goal Required</span>
          </div>
        ) : (
          <div className="space-y-3">
            {currentGoals.map((g, idx) => {
              const isNewlyAdded = !g.id || g.id.startsWith('GOL-NEW-') || g.action === 'ADD';
              const currentAction = g.action || (isNewlyAdded ? 'ADD' : 'CONTINUE');

              return (
                <div
                  key={idx}
                  className={`p-3.5 rounded-xl border space-y-2.5 transition-colors ${
                    currentAction === 'DISCONTINUE'
                      ? 'bg-rose-950/40 border-rose-700/60'
                      : currentAction === 'MET'
                      ? 'bg-emerald-950/40 border-emerald-700/60'
                      : currentAction === 'MODIFY'
                      ? 'bg-amber-950/40 border-amber-700/60'
                      : 'bg-[#121c2e] border-slate-700/70'
                  }`}
                >
                  <div className="flex flex-wrap justify-between items-center gap-2">
                    <div className="flex items-center space-x-2">
                      <span className="font-bold text-slate-100 text-xs">Goal #{idx + 1}</span>
                      {g.id && (
                        <span className="text-[10px] font-mono px-1.5 py-0.5 rounded border border-slate-300 bg-slate-50 text-slate-600">
                          {g.id}
                        </span>
                      )}
                      <span
                        className={`text-[10px] font-semibold px-1.5 py-0.5 rounded border uppercase tracking-wider ${
                          currentAction === 'CONTINUE'
                            ? 'bg-slate-50 text-slate-700 border-slate-300'
                            : currentAction === 'MODIFY'
                            ? 'bg-amber-50 text-amber-900 border-amber-300'
                            : currentAction === 'MET'
                            ? 'bg-emerald-50 text-emerald-800 border-emerald-200'
                            : currentAction === 'DISCONTINUE'
                            ? 'bg-rose-50 text-rose-800 border-rose-200'
                            : 'bg-slate-50 text-slate-700 border-slate-300'
                        }`}
                      >
                        {currentAction === 'CONTINUE' ? 'Continuing' :
                         currentAction === 'MODIFY' ? 'Modify / Replace' :
                         currentAction === 'MET' ? 'Met' :
                         currentAction === 'DISCONTINUE' ? 'Discontinued' : 'New Goal'}
                      </span>
                    </div>

                    {!readOnly && (
                      <div className="w-full sm:w-auto flex items-center justify-end sm:justify-start">
                        {!isNewlyAdded ? (
                          <div className="grid grid-cols-2 sm:flex sm:items-center w-full sm:w-auto bg-slate-100 rounded-lg border border-slate-200 p-0.5 text-[11px] gap-0.5 sm:gap-0">
                            <button
                              type="button"
                              onClick={() => setGoalAction(idx, 'CONTINUE')}
                              className={`px-2 py-1.5 sm:px-2.5 sm:py-1 rounded font-medium transition-colors text-center whitespace-nowrap ${
                                currentAction === 'CONTINUE'
                                  ? 'bg-sky-600 text-white font-bold shadow-xs'
                                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                              }`}
                            >
                              Continue
                            </button>
                            <button
                              type="button"
                              onClick={() => setGoalAction(idx, 'MODIFY')}
                              className={`px-2 py-1.5 sm:px-2.5 sm:py-1 rounded font-medium transition-colors text-center whitespace-nowrap ${
                                currentAction === 'MODIFY'
                                  ? 'bg-amber-600 text-white font-bold shadow-xs'
                                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                              }`}
                            >
                              Modify / Replace
                            </button>
                            <button
                              type="button"
                              onClick={() => setGoalAction(idx, 'MET')}
                              className={`px-2 py-1.5 sm:px-2.5 sm:py-1 rounded font-medium transition-colors text-center whitespace-nowrap ${
                                currentAction === 'MET'
                                  ? 'bg-emerald-600 text-white font-bold shadow-xs'
                                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                              }`}
                            >
                              Met
                            </button>
                            <button
                              type="button"
                              onClick={() => setGoalAction(idx, 'DISCONTINUE')}
                              className={`px-2 py-1.5 sm:px-2.5 sm:py-1 rounded font-medium transition-colors text-center whitespace-nowrap ${
                                currentAction === 'DISCONTINUE'
                                  ? 'bg-rose-600 text-white font-bold shadow-xs'
                                  : 'text-slate-600 hover:text-slate-900 hover:bg-slate-200'
                              }`}
                            >
                              Discontinue
                            </button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => removeAddedGoal(idx)}
                            className="text-rose-400 hover:text-rose-300 p-1.5 rounded hover:bg-rose-950/40 text-[11px] flex items-center space-x-1 ml-auto sm:ml-0"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                            <span>Remove Unsaved</span>
                          </button>
                        )}
                      </div>
                    )}
                  </div>

                  {currentAction === 'MODIFY' && (
                    <div className="p-2.5 bg-amber-50 rounded-lg text-[11px] text-amber-900 border border-amber-300 font-medium">
                      Note: You are modifying this goal. The current wording will be preserved historically as <strong>SUPERSEDED</strong>, and this new description will become the successor canonical goal linked to parent <strong>{g.id}</strong> upon POC finalization.
                    </div>
                  )}

                  <label className="block text-[10px] font-semibold text-slate-200">Goal designation</label>
                  <select
                    disabled={readOnly}
                    value={g.goal_term || ''}
                    onChange={(e) => updateGoal(idx, 'goal_term', e.target.value)}
                    className="w-full sm:w-48 bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100"
                  >
                    <option value="">Not designated</option>
                    <option value="LONG">Long-term</option>
                    <option value="SHORT">Short-term</option>
                  </select>
                  <textarea
                    disabled={readOnly || currentAction === 'DISCONTINUE' || currentAction === 'MET'}
                    rows={2}
                    value={g.description}
                    onChange={(e) => updateGoal(idx, 'description', e.target.value)}
                    className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                  />
                  <div className="grid grid-cols-1 sm:grid-cols-4 gap-2.5 text-xs">
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-200 mb-1">Baseline</label>
                      <input
                        type="text"
                        disabled={readOnly || currentAction === 'DISCONTINUE'}
                        value={g.baseline || ''}
                        onChange={(e) => updateGoal(idx, 'baseline', e.target.value)}
                        className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-200 mb-1">Current Accuracy (%)</label>
                      <input
                        type="number"
                        disabled={readOnly}
                        value={g.current_percentage || 0}
                        onChange={(e) => updateGoal(idx, 'current_percentage', Number(e.target.value))}
                        className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-200 mb-1">Target Accuracy (%)</label>
                      <input
                        type="number"
                        disabled={readOnly || currentAction === 'DISCONTINUE'}
                        value={g.target_percentage || 80}
                        onChange={(e) => updateGoal(idx, 'target_percentage', Number(e.target.value))}
                        className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                      />
                    </div>
                    <div>
                      <label className="block text-[10px] font-semibold text-slate-200 mb-1">Target Date</label>
                      <input
                        type="date"
                        disabled={readOnly || currentAction === 'DISCONTINUE'}
                        value={g.target_date || ''}
                        onChange={(e) => updateGoal(idx, 'target_date', e.target.value)}
                        className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-mono font-bold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
                      />
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* 5. Prescribed Dosage & Frequency */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <Calendar className="w-4 h-4 text-sky-400" />
          <span>5. Prescribed Treatment Dosage & Frequency</span>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Prescribed Frequency</label>
            <input
              type="text"
              disabled={readOnly}
              value={data.frequency || '2x/week'}
              onChange={(e) => updateField('frequency', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-semibold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Session Duration</label>
            <input
              type="text"
              disabled={readOnly}
              value={data.duration || (data.duration_minutes ? `${data.duration_minutes} minutes` : (client?.expected_duration_minutes ? `${client.expected_duration_minutes} minutes` : '60 minutes'))}
              onChange={(e) => updateField('duration', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 font-semibold focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Treatment Setting</label>
            <input
              type="text"
              disabled={readOnly}
              value={data.setting || 'Natural Home Environment / Telepractice'}
              onChange={(e) => updateField('setting', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>
      </div>

      {/* 6. Home Program & Prognosis */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center justify-between pb-2 border-b border-slate-700/60">
          <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider">
            <HeartHandshake className="w-4 h-4 text-sky-400" />
            <span>6. Home Program Carryover Plan & Prognosis</span>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1 uppercase tracking-wider">
              Updated Home Carryover Strategies
            </label>
            <textarea
              disabled={readOnly}
              rows={3}
              value={data.home_program_carryover || ''}
              onChange={(e) => updateField('home_program_carryover', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1 uppercase tracking-wider">
              Updated Discharge Criteria & Prognosis
            </label>
            <textarea
              disabled={readOnly}
              rows={3}
              value={data.discharge_plan || ''}
              onChange={(e) => updateField('discharge_plan', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded-lg p-2.5 text-xs text-slate-100 placeholder:text-slate-500 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none leading-relaxed disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>
      </div>

      {/* 7. Physician Recertification Block */}
      <div className="p-4 sm:p-5 rounded-xl bg-gradient-to-b from-[#18263e] via-[#142034] to-[#101a2b] text-white border border-slate-700/80 shadow-[0_8px_24px_rgba(0,0,0,0.45),inset_0_1px_0_rgba(255,255,255,0.06)] space-y-3">
        <div className="flex items-center space-x-2 text-sky-400 font-bold text-xs uppercase tracking-wider pb-2 border-b border-slate-700/60">
          <UserCheck className="w-4 h-4 text-sky-400" />
          <span>7. Ordering Physician Recertification & Faxage Routing</span>
        </div>
        <p className="text-[11px] text-slate-300 leading-relaxed">
          This POC will generate a physician certification cover page with direct signature lines routed through Allegiant's Faxage electronic fax gateway.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Physician Name *</label>
            <input
              type="text"
              disabled={readOnly}
              value={data.physician_name || client?.physician_name || ''}
              onChange={(e) => updateField('physician_name', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none font-semibold disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Physician NPI</label>
            <input
              type="text"
              disabled={readOnly}
              value={data.physician_npi || client?.physician_npi || ''}
              onChange={(e) => updateField('physician_npi', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none font-mono font-bold disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
          <div>
            <label className="block text-[10px] font-semibold text-slate-200 mb-1">Physician Fax (for POC routing)</label>
            <input
              type="text"
              disabled={readOnly}
              value={data.physician_fax || client?.physician_fax || ''}
              onChange={(e) => updateField('physician_fax', e.target.value)}
              className="w-full bg-[#0a101b] border border-slate-700/80 rounded px-2.5 py-1.5 text-xs text-slate-100 focus:bg-[#0c1424] focus:border-sky-500 focus:ring-1 focus:ring-sky-500/30 focus:outline-none font-mono font-bold disabled:bg-slate-900/80 disabled:text-slate-500 shadow-inner"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
